/**
 * Referral Dashboard Demo - For Marketing Screenshots
 * Shows mockup data without authentication
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign, 
  Users, 
  Link as LinkIcon, 
  Copy, 
  CheckCircle,
  TrendingUp,
  Share2,
  Crown,
  Medal,
  Bell
} from 'lucide-react';

const ReferralDashboardDemo = () => {
  const [copied, setCopied] = useState(false);

  // Demo mockup data
  const stats = {
    total_signups: 47,
    active_referrals: 38,
    pending_payout: 1247.50,
    lifetime_earnings: 4892.75
  };

  const referral_code = "SARAH-VIP25";
  const referral_link = "https://apcestateflow.com/signup?ref=SARAH-VIP25";

  const recent_signups = [
    { name: "Michael Chen", plan: "HOA Community", date: "Dec 15, 2024", status: "active", commission: "$150.00" },
    { name: "Sarah Williams", plan: "Landlord", date: "Dec 12, 2024", status: "active", commission: "$49.75" },
    { name: "James Rodriguez", plan: "Property Manager", date: "Dec 10, 2024", status: "active", commission: "$95.00" },
    { name: "Emily Thompson", plan: "HOA Community", date: "Dec 8, 2024", status: "active", commission: "$150.00" },
    { name: "David Kim", plan: "Landlord", date: "Dec 5, 2024", status: "active", commission: "$49.75" }
  ];

  const leaderboard = [
    { rank: 1, name: "You (Sarah)", referrals: 47, earnings: "$4,892.75", isYou: true },
    { rank: 2, name: "Alex Partner", referrals: 35, earnings: "$3,654.00" },
    { rank: 3, name: "Maria Realty", referrals: 28, earnings: "$2,847.50" },
    { rank: 4, name: "John Broker", referrals: 22, earnings: "$2,156.25" },
    { rank: 5, name: "Lisa Agent", referrals: 18, earnings: "$1,823.00" }
  ];

  const earningsChart = [
    { month: "Jul", earnings: 245 },
    { month: "Aug", earnings: 387 },
    { month: "Sep", earnings: 512 },
    { month: "Oct", earnings: 689 },
    { month: "Nov", earnings: 892 },
    { month: "Dec", earnings: 1247 }
  ];

  const copyToClipboard = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const maxEarnings = Math.max(...earningsChart.map(d => d.earnings));

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center text-gray-900">
              <TrendingUp className="h-8 w-8 mr-3 text-emerald-600" />
              Referral Dashboard
            </h1>
            <p className="text-gray-600 mt-1 text-lg">Share your link and earn 25% recurring commissions</p>
          </div>
          <Button className="flex items-center bg-emerald-600 hover:bg-emerald-700">
            <Bell className="h-4 w-4 mr-2" /> Notifications On
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Signups</p>
                  <p className="text-4xl font-bold text-gray-900">{stats.total_signups}</p>
                  <p className="text-xs text-green-600 mt-1">↑ 12 this month</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Active Referrals</p>
                  <p className="text-4xl font-bold text-gray-900">{stats.active_referrals}</p>
                  <p className="text-xs text-green-600 mt-1">81% retention rate</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Pending Payout</p>
                  <p className="text-4xl font-bold text-green-600">${stats.pending_payout.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">Request payout anytime</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <DollarSign className="h-8 w-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-blue-600 shadow-lg border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-100 font-medium">Lifetime Earnings</p>
                  <p className="text-4xl font-bold text-white">${stats.lifetime_earnings.toFixed(2)}</p>
                  <p className="text-xs text-emerald-100 mt-1">🎉 Top 5% of partners!</p>
                </div>
                <div className="bg-white/20 p-3 rounded-full">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="chart">📊 Earnings Chart</TabsTrigger>
            <TabsTrigger value="leaderboard">🏆 Leaderboard</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Referral Link Card */}
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <LinkIcon className="h-5 w-5 mr-2 text-emerald-600" />
                  Your Referral Link
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input 
                    value={referral_link} 
                    readOnly 
                    className="flex-1 bg-gray-50 text-base font-mono"
                  />
                  <Button 
                    onClick={copyToClipboard}
                    className={copied ? "bg-green-600" : "bg-emerald-600 hover:bg-emerald-700"}
                  >
                    {copied ? <CheckCircle className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                
                <div className="flex items-center gap-4 pt-2">
                  <span className="text-sm text-gray-600">Share on:</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="hover:bg-blue-50 hover:text-blue-600">
                      <Share2 className="h-4 w-4 mr-1" /> Twitter
                    </Button>
                    <Button size="sm" variant="outline" className="hover:bg-blue-50 hover:text-blue-600">
                      <Share2 className="h-4 w-4 mr-1" /> Facebook
                    </Button>
                    <Button size="sm" variant="outline" className="hover:bg-green-50 hover:text-green-600">
                      <Share2 className="h-4 w-4 mr-1" /> WhatsApp
                    </Button>
                    <Button size="sm" variant="outline" className="hover:bg-blue-50 hover:text-blue-600">
                      <Share2 className="h-4 w-4 mr-1" /> LinkedIn
                    </Button>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-emerald-800">
                    <strong>Your Code:</strong> <span className="font-mono bg-white px-2 py-1 rounded">{referral_code}</span>
                    <span className="ml-4">|</span>
                    <span className="ml-4"><strong>Commission Rate:</strong> 25% recurring</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Recent Signups */}
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center text-xl">
                  <Users className="h-5 w-5 mr-2 text-blue-600" />
                  Recent Signups
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-gray-500 text-sm">
                        <th className="pb-3 font-medium">Name</th>
                        <th className="pb-3 font-medium">Plan</th>
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium">Commission</th>
                        <th className="pb-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recent_signups.map((signup, index) => (
                        <tr key={index} className="border-b last:border-0">
                          <td className="py-4 font-medium text-gray-900">{signup.name}</td>
                          <td className="py-4">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {signup.plan}
                            </Badge>
                          </td>
                          <td className="py-4 text-gray-600">{signup.date}</td>
                          <td className="py-4 font-semibold text-green-600">{signup.commission}</td>
                          <td className="py-4">
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" /> Active
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="chart">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle>Monthly Earnings Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-end justify-between gap-4 px-4">
                  {earningsChart.map((data, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <span className="text-sm font-semibold text-emerald-600 mb-2">${data.earnings}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t-lg transition-all duration-500"
                        style={{ height: `${(data.earnings / maxEarnings) * 180}px` }}
                      />
                      <span className="text-sm text-gray-600 mt-2 font-medium">{data.month}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-center">
                  <p className="text-lg text-gray-600">
                    <span className="text-emerald-600 font-bold text-2xl">+408%</span> earnings growth in 6 months!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leaderboard">
            <Card className="bg-white shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center">
                  🏆 Top Referral Partners
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {leaderboard.map((partner, index) => (
                    <div 
                      key={index} 
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        partner.isYou 
                          ? 'bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-300' 
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          partner.rank === 1 ? 'bg-yellow-100' :
                          partner.rank === 2 ? 'bg-gray-200' :
                          partner.rank === 3 ? 'bg-orange-100' : 'bg-gray-100'
                        }`}>
                          {partner.rank === 1 ? <Crown className="h-5 w-5 text-yellow-600" /> :
                           partner.rank === 2 ? <Medal className="h-5 w-5 text-gray-500" /> :
                           partner.rank === 3 ? <Medal className="h-5 w-5 text-orange-500" /> :
                           <span className="font-bold text-gray-600">#{partner.rank}</span>}
                        </div>
                        <div>
                          <p className={`font-semibold ${partner.isYou ? 'text-emerald-700' : 'text-gray-900'}`}>
                            {partner.name}
                            {partner.isYou && <Badge className="ml-2 bg-emerald-500">That's You!</Badge>}
                          </p>
                          <p className="text-sm text-gray-500">{partner.referrals} referrals</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xl font-bold ${partner.isYou ? 'text-emerald-600' : 'text-gray-900'}`}>
                          {partner.earnings}
                        </p>
                        <p className="text-xs text-gray-500">lifetime earnings</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Request Payout Button */}
        <div className="flex justify-center">
          <Button size="lg" className="bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white px-8 py-6 text-lg shadow-lg">
            <DollarSign className="h-5 w-5 mr-2" />
            Request Payout ($1,247.50 available)
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralDashboardDemo;
